import { makeModal } from "../utils/make_modal";
import { useApiGet, useApiMutation } from "../utils/api";
import { EditableField } from "./editable_field";
import { Modal } from "react-bootstrap";
import { EllipsisMenu } from "../utils/ellipsis_menu";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import CreatableSelect from "react-select/creatable";

const useClipboardPasteImages = (onPaste: (formData: FormData) => void) => {
  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      const clipboardItems = event.clipboardData.items;

      Array.from(clipboardItems)
        .filter((item) => item.type.startsWith("image/"))
        .map((item) => item.getAsFile())
        .forEach(async (blob) => {
          const formData = new FormData();
          formData.append("image", blob);
          onPaste(formData);
        });
    };

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, []);
};

const orderImagesForLightbox = (images: any[], selected: string) => {
  const index = images.findIndex((image) => image.path === selected);
  return [...images.slice(index), ...images.slice(0, index)];
};

export const usePageModal = () =>
  makeModal(({ refetch: refetchParent, close, object }) => {
    const { data, refetch: refetchSelf, unavailable } = useApiGet(object);
    const { post, put, destroy } = useApiMutation();
    const [dragging, setDragging] = useState(false);
    const [lightbox, setLightbox] = useState<string | null>(null);

    useClipboardPasteImages((formData) => {
      post(`${object}/images.json`, formData).success(() => refetchSelf());
    });

    if (unavailable) {
      return unavailable;
    }

    const handleFileDrop = (e: DragEvent) => {
      e.preventDefault();
      Array.from(e.dataTransfer.files).forEach((file) => {
        const formData = new FormData();
        formData.append("image", file);
        post(`${object}/images`, formData).success(() => refetchSelf());
      });
    };

    const update = (values: any) => {
      put(object, { page: values }).success(refetchSelf);
    };

    const destroyPage = () => {
      destroy(object).success(close);
    };

    const destroyImage = (image: any) => {
      destroy(image.path).success(refetchSelf);
    };

    const { page } = data;

    return (
      <Modal show={!!object} onHide={close} size="lg">
        <Modal.Header>
          <Modal.Title className="w-100">
            <div className="d-flex align-items-center justify-content-between">
              <EditableField
                value={page?.name}
                onSubmit={(value) => update({ name: value })}
              />
              <EllipsisMenu
                actions={[
                  {
                    label: "Delete",
                    className: "text-danger",
                    action: () => confirm("Are you sure?") && destroyPage(),
                  },
                ]}
              />
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pb-5">
          <div className="mb-3">
            <label className="form-label fw-bold">Notes</label>
            <div className="min-h-100px">
              <EditableField
                value={page?.notes}
                onSubmit={(value) => update({ notes: value })}
                type="textarea"
                rows={10}
                displayClassName="pre-wrap fs-12 monospace"
                fieldClassName="fs-12 monospace"
              />
            </div>
          </div>
          <hr className="my-3" />
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              setDragging(false);
              handleFileDrop(e as unknown as DragEvent);
            }}
          >
            <label className="form-label fw-bold">Images</label>
            {page?.images?.length === 0 && <div className="gray">Add images</div>}
            <div className="d-flex flex-wrap align-items-center min-h-100px">
              {page?.images.map((image) => (
                <div className="d-flex flex-column align-items-center mh-2">
                  <img
                    className="uploaded-image"
                    src={image.path}
                    alt="Image"
                    onClick={() => setLightbox(image.path)}
                  />
                  <Trash2
                    size={12}
                    onClick={() => confirm("Delete?") && destroyImage(image)}
                    className="mt-1 text-danger pointer"
                  />
                </div>
              ))}
              {dragging && <div className="w-75px h-75px border-dashed mh-2" />}
            </div>
          </div>

          <hr className="my-3" />

          <div className="mb-3">
            <label className="form-label fw-bold">Tags</label>
            <div className="min-h-100px">
              <CreatableSelect
                isClearable
                options={[]}
                isMulti
                value={page?.tags.map((tag) => ({ label: tag, value: tag }))}
                onChange={(value) => update({ tags: value.map((v) => v.value) })}
              />
            </div>
          </div>

          <Lightbox
            open={!!lightbox}
            close={() => setLightbox(null)}
            slides={orderImagesForLightbox(page?.images, lightbox).map((image) => ({
              src: image.path,
            }))}
          />
        </Modal.Body>
      </Modal>
    );
  });
